import {formatMoney} from '@shopify/theme-currency';

import throttle from '../util/throttle';
import * as a11y from '../vendor/theme-scripts/theme-a11y';
import FetchError from '../util/fetch-error';
import {ShippingCalculator} from '../slate/shipping-calculator';
import {Slider} from '../features/slider';

const classes = {
  animated: 'is-animated',
  active: 'is-active',
  added: 'is-added',
  disabled: 'is-disabled',
  error: 'has-error',
  headerStuck: 'js__header__stuck',
  hidden: 'is-hidden',
  hiding: 'is-hiding',
  loading: 'is-loading',
  open: 'is-open',
  removed: 'is-removed',
  success: 'is-success',
  visible: 'is-visible',
  focused: 'is-focused',
  expanded: 'is-expanded',
  updated: 'is-updated',
  variantSoldOut: 'variant--soldout',
  variantUnavailable: 'variant--unavailable',
};

const selectors = {
  apiContent: '[data-api-content]',
  apiLineItems: '[data-api-line-items]',
  apiUpsellItems: '[data-api-upsell-items]',
  apiCartPrice: '[data-api-cart-price]',
  apiCartRecommended: '[data-api-recommended-items]',
  animation: '[data-animation]',
  additionalCheckoutButtons: '.additional-checkout-buttons',
  buttonHolder: '[data-foot-holder]',
  buttonSkipUpsellProduct: '[data-skip-upsell-product]',
  cartBarAdd: '[data-add-to-cart-bar]',
  cartCloseError: '[data-cart-error-close]',
  cartDrawer: '[data-cart-drawer]',
  cartDrawerClose: '[data-cart-drawer-close]',
  cartEmpty: '[data-cart-empty]',
  cartErrors: '[data-cart-errors]',
  cartItemRemove: '[data-item-remove]',
  cartPage: '[data-cart-page]',
  cartForm: '[data-cart-form]',
  cartTermsCheckbox: '[data-cart-acceptance-checkbox]',
  cartCheckoutButtonWrapper: '[data-cart-checkout-buttons]',
  cartCheckoutButton: '[data-cart-checkout-button]',
  cartToggle: '[data-cart-toggle]',
  cartTotal: '[data-cart-total]',
  errorMessage: '[data-error-message]',
  formCloseError: '[data-close-error]',
  formErrorsContainer: '[data-cart-errors-container]',
  formWrapper: '[data-form-wrapper]',
  freeShipping: '[data-free-shipping]',
  freeShippingGraph: '[data-progress-graph]',
  freeShippingProgress: '[data-progress-bar]',
  headerWrapper: '[data-header-wrapper]',
  item: '[data-item]',
  itemsHolder: '[data-items-holder]',
  leftToSpend: '[data-left-to-spend]',
  navDrawer: '[data-drawer]',
  outerSection: '[data-section-id]',
  priceHolder: '[data-cart-price-holder]',
  quickAddHolder: '[data-quick-add-holder]',
  quickAddModal: '[data-quick-add-modal]',
  qtyInput: 'input[name="updates[]"]',
  upsellProductsHolder: '[data-upsell-products]',
  upsellWidget: '[data-upsell-widget]',
  termsErrorMessage: '[data-terms-error-message]',
  dataSlider: '[data-cart-recommended]',
  dataSliders: '[data-cart-recommended-sliders]',
  dataGwp: '[data-gwp]',
  dataGwpLimit: '[data-gwp-limit]',
  dataDefaultMessage: '[data-default-message]'
};

const attributes = {
  cartToggle: 'data-cart-toggle',
  cartTotal: 'data-cart-total',
  disabled: 'disabled',
  drawerUnderlay: 'data-drawer-underlay',
  freeShipping: 'data-free-shipping',
  freeShippingLimit: 'data-free-shipping-limit',
  item: 'data-item',
  itemIndex: 'data-item-index',
  itemTitle: 'data-item-title',
  quickAddHolder: 'data-quick-add-holder',
  quickAddVariant: 'data-quick-add-variant',
  dataGwpLimit: 'data-gwp-limit',
  progressFirstLimit: 'data-first-limit',
  progressMessage1: 'data-message-step-1',
  progressMessage2: 'data-message-step-2',
  progressMessage3: 'data-message-step-3'
};

class CartDrawer {
  constructor() {
    if (window.location.pathname.endsWith('/password')) {
      return;
    }

    this.init();
  }

  init() {
    // DOM Elements
    this.cartPage = document.querySelector(selectors.cartPage);
    this.cartForm = document.querySelector(selectors.cartForm);
    this.cartDrawer = document.querySelector(selectors.cartDrawer);
    this.cartDrawerClose = document.querySelector(selectors.cartDrawerClose);
    this.cartEmpty = document.querySelector(selectors.cartEmpty);
    this.cartTermsCheckbox = document.querySelector(selectors.cartTermsCheckbox);
    this.cartCheckoutButtonWrapper = document.querySelector(selectors.cartCheckoutButtonWrapper);
    this.cartCheckoutButton = document.querySelector(selectors.cartCheckoutButton);
    this.buttonHolder = document.querySelector(selectors.buttonHolder);
    this.itemsHolder = document.querySelector(selectors.itemsHolder);
    this.priceHolder = document.querySelector(selectors.priceHolder);
    this.items = document.querySelectorAll(selectors.item);
    this.cartTotal = document.querySelector(selectors.cartTotal);
    this.freeShipping = document.querySelectorAll(selectors.freeShipping);
    this.cartErrorHolder = document.querySelector(selectors.cartErrors);
    this.cartCloseErrorMessage = document.querySelector(selectors.cartCloseError);
    this.headerWrapper = document.querySelector(selectors.headerWrapper);
    this.accessibility = a11y;
    this.navDrawer = document.querySelector(selectors.navDrawer);
    this.upsellProductsHolder = document.querySelector(selectors.upsellProductsHolder);
    this.upsellSlidersHolder = document.querySelector(selectors.dataSliders);
    this.subtotal = window.theme.subtotal;
    this.gwpLimit = document.querySelector(selectors.dataGwpLimit);

    // Define Cart object depending on if we have cart drawer or cart page
    this.cart = this.cartDrawer || this.cartPage;

    // Cart events
    this.cartAddEvent = this.cartAddEvent.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.updateProgress = this.updateProgress.bind(this);

    // Set global event listeners for "Add to cart" and Announcement bar wheel progress
    document.addEventListener('theme:cart:add', this.cartAddEvent);
    document.addEventListener('theme:announcement:init', this.updateProgress);

    // Upsell products
    this.skipUpsellProductsArray = [];
    this.skipUpsellProductEvent();
    this.checkSkippedUpsellProductsFromStorage();
    this.toggleCartUpsellWidgetVisibility();
    this.initUpsellSliders();

    // Free Shipping values
    this.circumference = 28 * Math.PI; // radius - stroke * 4 * PI
    this.freeShippingLimit = this.freeShipping.length ? Number(this.freeShipping[0].getAttribute(attributes.freeShippingLimit)) * 100 * window.Shopify.currency.rate : 0;
    this.gwpLimit = this.gwpLimit ? Number(this.gwpLimit.getAttribute(attributes.dataGwpLimit)) : 0;
    this.freeShippingMessageHandle(this.subtotal);
    this.updateProgress();

    // Don't run any cart JS if Cart drawer is disabled and you are not on the Cart page
    if (!this.cart) return;

    this.build = this.build.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.productAddCallback = this.productAddCallback.bind(this);
    this.openCartDrawer = this.openCartDrawer.bind(this);
    this.closeCartDrawer = this.closeCartDrawer.bind(this);
    this.toggleCartDrawer = this.toggleCartDrawer.bind(this);
    this.openCartDrawerOnProductAdded = this.openCartDrawerOnProductAdded.bind(this);
    this.animateItems = this.animateItems.bind(this);
    this.showAnimatedItems = this.showAnimatedItems.bind(this);
    this.formSubmitHandler = throttle(this.formSubmitHandler.bind(this), 50);

    if (this.cartPage) {
      this.showAnimatedItems();
    }

    // Checking
    this.hasItemsInCart = this.hasItemsInCart.bind(this);
    this.cartCount = this.getCartItemCount();

    // Set classes
    this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

    // Flags
    this.totalItems = this.items.length;
    this.cartDrawerIsOpen = false;
    this.cartDrawerEnabled = theme.settings.cartDrawerEnabled;
    this.cartUpdateFailed = false;

    // Cart Events
    this.cartEvents();
    this.cartToggleEvents();
    this.cartRemoveEvents();
    this.cartUpdateEvents();

    document.addEventListener('theme:cart:toggle', this.toggleCartDrawer);
    document.addEventListener('theme:quick-add:open', this.closeCartDrawer);
    document.addEventListener('theme:product:add', this.productAddCallback);
    document.addEventListener('theme:product:add-error', this.productAddCallback);
    document.addEventListener('theme:product:added', this.openCartDrawerOnProductAdded);

    this.checkGwp()
  }

  /**
   * Cart update event hook
   *
   * @return  {Void}
   */

  cartUpdateEvents() {
    this.items = document.querySelectorAll(selectors.item);

    this.items.forEach((item) => {
      item.addEventListener('theme:cart:update', (event) => {
        let bundleData = null;
        const isBundle = item.hasAttribute('data-bundle-item');
        let updateData = {
          id: event.detail.id,
          quantity: event.detail.quantity,
        };

        if (item.hasAttribute('data-bundle-item')) {
          let updateDataBundle = JSON.parse(item.getAttribute('data-bundle-item'));
          Object.keys(updateDataBundle).forEach(key => updateDataBundle[key] = event.detail.quantity);
          updateData = {
            updates: updateDataBundle
          }
        }


        this.updateCart(updateData, item, isBundle);
      });
    });
  }

  /**
   * Cart events
   *
   * @return  {Void}
   */

  cartRemoveEvents() {
    const cartItemRemove = document.querySelectorAll(selectors.cartItemRemove);

    cartItemRemove.forEach((button) => {
      const item = button.closest(selectors.item);
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const isBundle = item.hasAttribute('data-bundle-item');
        let updateData = {
          id: button.dataset.id,
          quantity: 0,
        };

        if (isBundle) {
          let updateDataBundle = JSON.parse(item.getAttribute('data-bundle-item'));
          Object.keys(updateDataBundle).forEach(key => updateDataBundle[key] = 0);

          updateData = {
            updates: updateDataBundle
          }
        }

        if (button.classList.contains(classes.disabled)) return;

        this.updateCart(updateData, item, isBundle);
      });
    });

    if (this.cartCloseErrorMessage) {
      this.cartCloseErrorMessage.addEventListener('click', (event) => {
        event.preventDefault();

        this.cartErrorHolder.classList.remove(classes.expanded);
      });
    }
  }

  /**
   * Cart event add product to cart
   *
   * @return  {Void}
   */

  cartAddEvent(event) {
    let formData = '';
    let jsonFormat = false
    let button = event.detail.button;
    if (button.hasAttribute('disabled')) return;
    const form = button.closest('form');
    // Validate form
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    formData = new FormData(form);
    if (form !== null && form.querySelector('[type="file"]')) {
      return;
    }

    if (event.detail.formData) {
      jsonFormat = true;
      formData = JSON.stringify(event.detail.formData)
    }
    event.preventDefault();
    this.addToCart(formData, button, jsonFormat);
  }

  /**
   * Cart events
   *
   * @return  {Void}
   */

  cartEvents() {
    if (this.cartTermsCheckbox) {
      this.cartTermsCheckbox.removeEventListener('change', this.formSubmitHandler);
      this.cartCheckoutButtonWrapper.removeEventListener('click', this.formSubmitHandler);
      this.cartForm.removeEventListener('submit', this.formSubmitHandler);

      this.cartTermsCheckbox.addEventListener('change', this.formSubmitHandler);
      this.cartCheckoutButtonWrapper.addEventListener('click', this.formSubmitHandler);
      this.cartForm.addEventListener('submit', this.formSubmitHandler);
    }
  }

  formSubmitHandler() {
    const termsAccepted = document.querySelector(selectors.cartTermsCheckbox).checked;
    const termsError = document.querySelector(selectors.termsErrorMessage);

    // Disable form submit if terms and conditions are not accepted
    if (!termsAccepted) {
      if (document.querySelector(selectors.termsErrorMessage).length > 0) {
        return;
      }

      termsError.innerText = theme.strings.cartAcceptanceError;
      this.cartCheckoutButton.setAttribute(attributes.disabled, true);
      termsError.classList.add(classes.expanded);
    } else {
      termsError.classList.remove(classes.expanded);
      this.cartCheckoutButton.removeAttribute(attributes.disabled);
    }
  }

  /**
   * Cart event remove out of stock error
   *
   * @return  {Void}
   */

  formErrorsEvents(errorContainer) {
    const buttonErrorClose = errorContainer.querySelector(selectors.formCloseError);
    buttonErrorClose?.addEventListener('click', (e) => {
      e.preventDefault();

      if (errorContainer) {
        errorContainer.classList.remove(classes.visible);
      }
    });
  }

  /**
   * Get response from the cart
   *
   * @return  {Void}
   */

  getCart() {
    fetch(theme.routes.cart_url + '?section_id=api-cart-items')
      .then(this.cartErrorsHandler)
      .then((response) => response.text())
      .then((response) => {
        const element = document.createElement('div');
        element.innerHTML = response;

        const cleanResponse = element.querySelector(selectors.apiContent);
        this.build(cleanResponse);
      })
      .catch((error) => console.log(error));
  }

  /**
   * Add item(s) to the cart and show the added item(s)
   *
   * @param   {String}  formData
   * @param   {DOM Element}  button
   *
   * @param {boolean} jsonDataFormat
   * @return  {Void}
   */

  addToCart(formData, button, jsonDataFormat = false) {
    if (this.cart) {
      this.cart.classList.add(classes.loading);
    }

    const quickAddHolder = button?.closest(selectors.quickAddHolder);

    if (this.cartDrawerEnabled) {
      if (button) {
        button.classList.add(classes.loading);
        button.disabled = true;
      }

      if (quickAddHolder) {
        quickAddHolder.classList.add(classes.visible);
      }
    }

    let headers = {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/javascript'
    }

    if (jsonDataFormat) {
      headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    }

    fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: headers,
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          this.addToCartError(response, button);
          this.enableCartButtons();

          return;
        }
        if (response.status) {
          this.addToCartError(response, button);

          if (button) {
            button.classList.remove(classes.loading);
            button.disabled = false;
          }

          return;
        }

        if (this.cartDrawerEnabled) {
          if (button) {
            button.classList.remove(classes.loading);
            button.classList.add(classes.added);

            button.dispatchEvent(
              new CustomEvent('theme:product:add', {
                detail: {
                  response: response,
                  button: button,
                },
                bubbles: true,
              })
            );
          }

          this.getCart();
        } else {
          // Redirect to cart page if "Add to cart" is successful
          window.location = theme.routes.cart_url;
        }
      })
      .catch((error) => {
        this.addToCartError(error, button);
        this.enableCartButtons();
      });
  }

  /**
   * Update cart
   *
   * @param   {Object}  updateData
   *
   * @return  {Void}
   */

  updateCart(updateData = {}, currentItem = null, isBundle = false) {
    this.cart.classList.add(classes.loading);

    let url = theme.routes.cart_change_url

    if (isBundle) url = theme.routes.cart_update_url

    let updatedQuantity = updateData.quantity;
    if (currentItem !== null) {
      if (updatedQuantity || isBundle) {
        currentItem.classList.add(classes.loading);
      } else {
        currentItem.classList.add(classes.removed);
      }
    }
    this.disableCartButtons();

    const newItem = this.cart.querySelector(`[${attributes.item}="${updateData.id}"]`) || currentItem;
    const lineIndex = newItem?.hasAttribute(attributes.itemIndex) ? parseInt(newItem.getAttribute(attributes.itemIndex)) : 0;
    const itemTitle = newItem?.hasAttribute(attributes.itemTitle) ? newItem.getAttribute(attributes.itemTitle) : null;

    if (lineIndex === 0) return;

    let data = {
      line: lineIndex,
      quantity: updatedQuantity,
    };

    if (isBundle) {
      data = updateData
    }


    fetch(url, {
      method: 'post',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(data),
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.cartUpdateFailed = true;
          this.updateErrorText(itemTitle);
          this.toggleErrorMessage();
          this.resetLineItem(currentItem);
          this.enableCartButtons();

          return;
        }

        this.getCart();
      })
      .catch((error) => {
        console.log(error);
        this.enableCartButtons();
      });
  }

  /**
   * Reset line item initial state
   *
   * @return  {Void}
   */
  resetLineItem(item) {
    const qtyInput = item.querySelector(selectors.qtyInput);
    const qty = qtyInput.getAttribute('value');
    qtyInput.value = qty;
    item.classList.remove(classes.loading);
  }

  /**
   * Disable cart buttons and inputs
   *
   * @return  {Void}
   */
  disableCartButtons() {
    const inputs = this.cart.querySelectorAll('input');
    const buttons = this.cart.querySelectorAll(`button, ${selectors.cartItemRemove}`);

    if (inputs.length) {
      inputs.forEach((item) => {
        item.classList.add(classes.disabled);
        item.blur();
        item.disabled = true;
      });
    }

    if (buttons.length) {
      buttons.forEach((item) => {
        item.setAttribute(attributes.disabled, true);
      });
    }
  }

  /**
   * Enable cart buttons and inputs
   *
   * @return  {Void}
   */
  enableCartButtons() {
    const inputs = this.cart.querySelectorAll('input');
    const buttons = this.cart.querySelectorAll(`button, ${selectors.cartItemRemove}`);

    if (inputs.length) {
      inputs.forEach((item) => {
        item.classList.remove(classes.disabled);
        item.disabled = false;
      });
    }

    if (buttons.length) {
      buttons.forEach((item) => {
        item.removeAttribute(attributes.disabled);
      });
    }

    this.cart.classList.remove(classes.loading);
  }

  /**
   * Update error text
   *
   * @param   {String}  itemTitle
   *
   * @return  {Void}
   */

  updateErrorText(itemTitle) {
    this.cartErrorHolder.querySelector(selectors.errorMessage).innerText = itemTitle;
  }

  /**
   * Toggle error message
   *
   * @return  {Void}
   */

  toggleErrorMessage() {
    if (!this.cartErrorHolder) return;

    this.cartErrorHolder.classList.toggle(classes.expanded, this.cartUpdateFailed);

    // Reset cart error events flag
    this.cartUpdateFailed = false;
  }

  /**
   * Handle errors
   *
   * @param   {Object}  response
   *
   * @return  {Object}
   */

  cartErrorsHandler(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  /**
   * Add to cart error handle
   *
   * @param   {Object}  data
   * @param   {DOM Element/Null} button
   *
   * @return  {Void}
   */

  addToCartError(data, button) {
    if (this.cartDrawerEnabled) {
      this.closeCartDrawer();
    }

    if (button) {
      const outerContainer = button.closest(selectors.outerSection) || button.closest(selectors.quickAddHolder) || button.closest(selectors.quickAddModal);
      let errorContainer = outerContainer?.querySelector(selectors.formErrorsContainer);
      const buttonUpsellHolder = button.closest(selectors.quickAddHolder);

      if (buttonUpsellHolder && buttonUpsellHolder.querySelector(selectors.formErrorsContainer)) {
        errorContainer = buttonUpsellHolder.querySelector(selectors.formErrorsContainer);
      }

      if (errorContainer) {
        const errorsText = data.message ? `${data.message}: ${data.description}`: data.errors
        errorContainer.innerHTML = `<div class="errors">${errorsText}<button type="button" class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" width="24px" height="24px" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel"><path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path></svg></button></div>`;
        errorContainer.classList.add(classes.visible);
        this.formErrorsEvents(errorContainer);
      }

      button.dispatchEvent(
        new CustomEvent('theme:product:add-error', {
          detail: {
            response: data,
            button: button,
          },
          bubbles: true,
        })
      );
    }

    const quickAddHolder = button?.closest(selectors.quickAddHolder);

    if (quickAddHolder) {
      quickAddHolder.dispatchEvent(
        new CustomEvent('theme:cart:error', {
          bubbles: true,
          detail: {
            message: data.message,
            description: data.description,
            holder: quickAddHolder,
          },
        })
      );
    }

    this.cart?.classList.remove(classes.loading);
  }

  /**
   * Add product to cart events
   *
   * @return  {Void}
   */
  productAddCallback(event) {
    let buttons = [];
    let quickAddHolder = null;
    const hasError = event.type == 'theme:product:add-error';
    const buttonATC = event.detail.button;
    const cartBarButtonATC = document.querySelector(selectors.cartBarAdd);

    buttons.push(buttonATC);
    quickAddHolder = buttonATC.closest(selectors.quickAddHolder);

    if (cartBarButtonATC) {
      buttons.push(cartBarButtonATC);
    }

    buttons.forEach((button) => {
      button.classList.remove(classes.loading);
      if (!hasError) {
        button.classList.add(classes.added);
      }
    });

    setTimeout(() => {
      buttons.forEach((button) => {
        button.classList.remove(classes.added);
        const isVariantUnavailable =
          button.closest(selectors.formWrapper)?.classList.contains(classes.variantSoldOut) || button.closest(selectors.formWrapper)?.classList.contains(classes.variantUnavailable);

        if (!isVariantUnavailable || button.closest(selectors.formWrapper).hasAttribute('data-form-wrapper-bundle')) {
          button.disabled = false;
        }
      });

      quickAddHolder?.classList.remove(classes.visible);
    }, 1000);
  }

  /**
   * Open cart drawer when product is added to cart
   *
   * @return  {Void}
   */
  openCartDrawerOnProductAdded() {
    if (this.cartDrawerIsOpen) {
      this.showAnimatedItems();
    } else {
      this.openCartDrawer();
    }
  }

  /**
   * Open cart drawer and add class on body
   *
   * @return  {Void}
   */

  openCartDrawer() {
    if (!this.cartDrawer) return;

    this.cartDrawerIsOpen = true;

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);

    document.dispatchEvent(new CustomEvent('theme:cart:open', {bubbles: true}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

    this.cartDrawer.classList.add(classes.open);

    // Observe Additional Checkout Buttons
    this.observeAdditionalCheckoutButtons();

    // Animate cart items
    // setTimeout is needed here to prevent animation issues on Safari
    setTimeout(this.showAnimatedItems);

    this.accessibility.trapFocus(this.cartDrawer, {
      elementToFocus: this.cartDrawer.querySelector(selectors.cartDrawerClose),
    });
  }

  /**
   * Close cart drawer and remove class on body
   *
   * @return  {Void}
   */

  closeCartDrawer() {
    if (!this.cartDrawer) return;
    if (!this.cartDrawer.classList.contains(classes.open)) return;

    this.cartDrawerIsOpen = false;

    document.dispatchEvent(
      new CustomEvent('theme:cart:close', {
        bubbles: true,
      })
    );

    // Cart elements animation reset
    this.resetAnimatedItems();
    this.itemsHolder.classList.remove(classes.updated);
    this.cartEmpty.classList.remove(classes.updated);
    this.cartErrorHolder.classList.remove(classes.expanded);
    this.cartDrawer.querySelectorAll(selectors.animation).forEach((item) => {
      const removeHidingClass = () => {
        item.classList.remove(classes.hiding);
        item.removeEventListener('animationend', removeHidingClass);
      };

      item.classList.add(classes.hiding);
      item.addEventListener('animationend', removeHidingClass);
    });
    this.cartDrawer.classList.remove(classes.open);
    this.accessibility.removeTrapFocus();
    document.body.removeEventListener('click', this.onBodyClickEvent);

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  /**
   * Toggle cart drawer
   *
   * @return  {Void}
   */

  toggleCartDrawer() {
    if (!this.cartDrawerIsOpen) {
      this.openCartDrawer();
    } else {
      this.closeCartDrawer();
    }
  }

  /**
   * Event click to element to open cart drawer
   *
   * @return  {Void}
   */

  cartToggleEvents() {
    if (this.cartDrawer) {
      this.cartDrawerClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeCartDrawer();
      });

      this.cartDrawer.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') {
          this.closeCartDrawer();
        }
      });
    }
  }

  onBodyClick(event) {
    if (event.target.hasAttribute(attributes.drawerUnderlay)) this.closeCartDrawer();
  }

  /**
   * Toggle classes on different containers and messages
   *
   * @return  {Void}
   */

  toggleClassesOnContainers() {
    const hasItemsInCart = this.hasItemsInCart();

    this.cartEmpty.classList.toggle(classes.hidden, hasItemsInCart);
    this.buttonHolder.classList.toggle(classes.hidden, !hasItemsInCart);
    this.itemsHolder.classList.toggle(classes.hidden, !hasItemsInCart);
  }

  /**
   * Build cart depends on results
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  build(data) {
    const cartItemsData = data.querySelector(selectors.apiLineItems);
    const upsellItemsData = data.querySelector(selectors.apiUpsellItems);
    const upsellSlidersData = data.querySelector(selectors.apiCartRecommended);
    const cartEmptyData = Boolean(cartItemsData === null && upsellItemsData === null);
    const priceData = data.querySelector(selectors.apiCartPrice);
    const cartTotal = data.querySelector(selectors.cartTotal);

    if (this.priceHolder && priceData) {
      this.priceHolder.innerHTML = priceData.innerHTML;
    }

    if (cartEmptyData) {
      this.itemsHolder.innerHTML = data;
      this.upsellProductsHolder.innerHTML = '';
      if (this.upsellSlidersHolder) {
        this.upsellSlidersHolder.innerHTML = '';
      }
    } else {
      this.itemsHolder.innerHTML = cartItemsData.innerHTML;
      this.upsellProductsHolder.innerHTML = upsellItemsData.innerHTML;
      if (this.upsellSlidersHolder) {
        this.upsellSlidersHolder.innerHTML = upsellSlidersData.innerHTML;
        this.initUpsellSliders()
      }
      this.skipUpsellProductEvent();
      this.checkSkippedUpsellProductsFromStorage();
      this.toggleCartUpsellWidgetVisibility();
    }

    this.newTotalItems = cartItemsData && cartItemsData.querySelectorAll(selectors.item).length ? cartItemsData.querySelectorAll(selectors.item).length : 0;
    this.subtotal = cartTotal && cartTotal.hasAttribute(attributes.cartTotal) ? parseInt(cartTotal.getAttribute(attributes.cartTotal)) : 0;
    this.cartCount = this.getCartItemCount();

    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: {
          cartCount: this.cartCount,
        },
      })
    );

    // Update cart total price
    this.cartTotal.innerHTML = this.subtotal === 0 ? window.theme.strings.free : formatMoney(this.subtotal, theme.moneyWithCurrencyFormat);

    // Remove cart loading class
    this.cart.classList.remove(classes.loading);

    if (this.totalItems !== this.newTotalItems) {
      this.totalItems = this.newTotalItems;

      this.toggleClassesOnContainers();
    }

    // Add class "is-updated" line items holder to reduce cart items animation delay via CSS variables
    if (this.cartDrawerIsOpen) {
      this.itemsHolder.classList.add(classes.updated);
    }

    // Prepare empty cart buttons for animation
    if (!this.hasItemsInCart()) {
      this.cartEmpty.querySelectorAll(selectors.animation).forEach((item) => {
        item.classList.remove(classes.animated);
      });
    }

    this.freeShippingMessageHandle(this.subtotal);
    this.cartRemoveEvents();
    this.cartUpdateEvents();
    this.toggleErrorMessage();
    this.enableCartButtons();
    this.updateProgress();

    document.dispatchEvent(
      new CustomEvent('theme:product:added', {
        bubbles: true,
      })
    );

    if (!this.cartDrawer) {
      this.showAnimatedItems();
    }

    this.checkGwp();
  }

  /**
   * Get cart item count
   *
   * @return  {Void}
   */

  getCartItemCount() {
    return parseInt(this.cart.querySelector('[data-cart-quantity]')?.getAttribute('data-cart-quantity')) || 0
    //return Array.from(this.cart.querySelectorAll(selectors.qtyInput)).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
  }

  /**
   * Check for items in the cart
   *
   * @return  {Void}
   */

  hasItemsInCart() {
    return this.totalItems > 0;
  }

  /**
   * Show/hide free shipping message
   *
   * @param   {Number}  total
   *
   * @return  {Void}
   */

  freeShippingMessageHandle(total) {
    if (!this.freeShipping.length) return;

    this.freeShipping.forEach((message) => {
      const hasQualifiedShippingMessage = message.hasAttribute(attributes.freeShipping) && message.getAttribute(attributes.freeShipping) === 'true' && total >= 0;
      message.classList.toggle(classes.success, hasQualifiedShippingMessage && total >= this.freeShippingLimit);
    });
  }

  /**
   * Update progress when update cart
   *
   * @return  {Void}
   */

  updateProgress() {
    this.freeShipping = document.querySelectorAll(selectors.freeShipping);

    if (!this.freeShipping.length) return;

    const percentValue = isNaN(this.subtotal / this.freeShippingLimit) ? 100 : this.subtotal / this.freeShippingLimit;
    const percent = Math.min(percentValue * 100, 100);
    const gwpPercentValue = isNaN(this.subtotal / this.gwpLimit) ? 100 : this.subtotal / this.gwpLimit;
    const gwpPercent = Math.min(gwpPercentValue * 100, 100);
    const dashoffset = this.circumference - ((percent / 100) * this.circumference) / 2;
    const leftToSpend = formatMoney(this.freeShippingLimit - this.subtotal, theme.moneyFormat);
    const gwpLeftToSpend = formatMoney(this.gwpLimit - this.subtotal, theme.moneyFormat);

    this.freeShipping.forEach((item) => {
      const progressBar = item.querySelector(selectors.freeShippingProgress);
      const progressGraph = item.querySelector(selectors.freeShippingGraph);
      const leftToSpendMessage = item.querySelector(selectors.leftToSpend);
      const firstLimit = item.getAttribute(attributes.progressFirstLimit);
      const messageFirsts = item.getAttribute(attributes.progressMessage1);
      const messageSecond = item.getAttribute(attributes.progressMessage2);
      const messageFinal = item.getAttribute(attributes.progressMessage3);
      const messageElem = item.querySelector(selectors.dataDefaultMessage);

      if (leftToSpendMessage) {
        leftToSpendMessage.innerHTML = leftToSpend.replace('.00', '');
      }

      let percentModify = percent;
      let gwp_message = '';

      if (firstLimit && firstLimit === 'gwp') {
        if (gwpPercent < 100) {
          percentModify = gwpPercent
          const leftAmount = gwpLeftToSpend.replace('.00', '')
          gwp_message = messageFirsts.replace('||amount||',  leftAmount);
        } else {
          percentModify = percent
          const leftAmount = leftToSpend.replace('.00', '')
          gwp_message = messageSecond.replace('||amount||',  leftAmount);
        }
      } else if (firstLimit && firstLimit === 'shipping') {
        if (percent < 100) {
          percentModify = percent
          const leftAmount = leftToSpend.replace('.00', '')
          gwp_message = messageFirsts.replace('||amount||',  leftAmount);
        } else {
          percentModify = gwpPercent
          const leftAmount = gwpLeftToSpend.replace('.00', '')
          gwp_message = messageSecond.replace('||amount||',  leftAmount);
        }
      }

      if  (gwpPercent >= 100 && percent >= 100) {
        gwp_message = messageFinal
      }

      // Set progress bar value
      if (progressBar) {
        if (gwp_message && this.gwpLimit) {
          item.classList.remove(classes.success);
          messageElem.textContent = gwp_message;
        }
        progressBar.value = percentModify;
      }

      // Set circle progress
      if (progressGraph) {
        progressGraph.style.setProperty('--stroke-dashoffset', `${dashoffset}`);
      }
    });
  }

  /**
   * Skip upsell product
   */
  skipUpsellProductEvent() {
    if (this.upsellProductsHolder === null) {
      return;
    }
    const skipButtons = this.upsellProductsHolder.querySelectorAll(selectors.buttonSkipUpsellProduct);

    if (skipButtons.length) {
      skipButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const productID = button.closest(selectors.quickAddHolder).getAttribute(attributes.quickAddHolder);

          if (!this.skipUpsellProductsArray.includes(productID)) {
            this.skipUpsellProductsArray.push(productID);
          }

          // Add skipped upsell product to session storage
          window.sessionStorage.setItem('skip_upsell_products', this.skipUpsellProductsArray);

          // Remove upsell product from cart
          this.removeUpsellProduct(productID);
          this.toggleCartUpsellWidgetVisibility();
        });
      });
    }
  }

  /**
   * Check for skipped upsell product added to session storage
   */
  checkSkippedUpsellProductsFromStorage() {
    const skippedUpsellItemsFromStorage = window.sessionStorage.getItem('skip_upsell_products');
    if (!skippedUpsellItemsFromStorage) return;

    const skippedUpsellItemsFromStorageArray = skippedUpsellItemsFromStorage.split(',');

    skippedUpsellItemsFromStorageArray.forEach((productID) => {
      if (!this.skipUpsellProductsArray.includes(productID)) {
        this.skipUpsellProductsArray.push(productID);
      }

      this.removeUpsellProduct(productID);
    });
  }

  removeUpsellProduct(productID) {
    if (!this.upsellProductsHolder) return;

    // Remove skipped upsell product from Cart
    const upsellProduct = this.upsellProductsHolder.querySelector(`[${attributes.quickAddHolder}="${productID}"]`);

    if (upsellProduct) {
      upsellProduct.parentNode.remove();
    }
  }

  /**
   * Show or hide cart upsell products widget visibility
   */
  toggleCartUpsellWidgetVisibility() {
    if (!this.upsellProductsHolder) return;

    // Hide upsell container if no items
    const upsellItems = this.upsellProductsHolder.querySelectorAll(selectors.quickAddHolder);
    const upsellWidget = this.upsellProductsHolder.closest(selectors.upsellWidget);

    if (!upsellWidget) return;

    upsellWidget.classList.toggle(classes.hidden, !upsellItems.length);
  }

  observeAdditionalCheckoutButtons() {
    // identify an element to observe
    const additionalCheckoutButtons = this.cartDrawer.querySelector(selectors.additionalCheckoutButtons);
    if (additionalCheckoutButtons) {
      // create a new instance of `MutationObserver` named `observer`,
      // passing it a callback function
      const observer = new MutationObserver(() => {
        this.accessibility.trapFocus(this.cartDrawer, {
          elementToFocus: this.cartDrawer.querySelector(selectors.cartDrawerClose),
        });
        observer.disconnect();
      });

      // call `observe()` on that MutationObserver instance,
      // passing it the element to observe, and the options object
      observer.observe(additionalCheckoutButtons, {subtree: true, childList: true});
    }
  }

  /**
   * Remove initially added AOS classes to allow animation on cart drawer open
   *
   * @return  {Void}
   */
  resetAnimatedItems() {
    this.cart.querySelectorAll(selectors.animation).forEach((item) => {
      item.classList.remove(classes.animated);
      item.classList.remove(classes.hiding);
    });
  }

  showAnimatedItems() {
    requestAnimationFrame(this.animateItems);
  }

  /**
   * Cart elements opening animation
   *
   * @return  {Void}
   */
  animateItems() {
    this.cart.querySelectorAll(selectors.animation).forEach((item) => {
      item.classList.add(classes.animated);
    });
  }

  initUpsellSliders() {
    const sliders = document.querySelectorAll(selectors.dataSlider);
    if (!sliders.length) return
    sliders.forEach(elem => {
      new Slider(elem);
    })
  }

  checkGwp() {
    const gwpElem = document.querySelector(selectors.dataGwp);
    if (!gwpElem) return

    const gwpData = JSON.parse(gwpElem.textContent);
    let gwpProductId = gwpData.gwpId

    // if (theme.gwpUpdatedId) {
    //   gwpProductId = theme.gwpUpdatedId;
    // }

    const gwpEvent = gwpData.gwpData;

    if (gwpEvent !== '' && gwpProductId) {
      if (gwpEvent === 'add') {
        let data = {
          'items': [{
            'id': gwpProductId,
            'quantity': 1,
            'properties': {
              '_gwp_product': 'true'
            }
          }]
        }

        this.addToCart(JSON.stringify(data), null, true)
      } else {
        let gwpItem = document.querySelector('[data-item-gwp-id]');
        if (gwpItem) {
          this.updateCart({
            id: gwpItem.getAttribute('data-item-gwp-id'),
            quantity: 0
          });
        }
      }
    }
  }
}

window.cart = new CartDrawer();

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
